/**
 * 計画マスタ 所属BG 編集 UI。
 *
 * 発電販売計画マスタ (gen) と需要調達計画マスタ (proc) の両 Create/Edit モーダルで共有する。
 *
 * 各行 = BG select + 有効期間(validFrom/validTo)。
 * 候補 filter = 同一エリア(areaCode === selfAreaCode) + 既選択除外。
 * 区分フィルタは backend (集約 RPC) が済ませた状態の bgMasters を受け取るため、
 * client-side では area narrow のみ行う。
 *
 * proc-plan の代表BG ロック (RG3):
 *   repBgMasterId が渡された場合、bgs[0] を代表BG として自動挿入 + ロック(select disabled、削除不可)。
 *   backend が bgs[0].BgMasterID == repBgMasterID を強制するため必須。
 */
import { useEffect, useMemo } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type Control,
  type FieldValues,
  useFieldArray,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  FieldRoot,
  HStack,
  Stack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { FieldErrorText } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { iconSize } from '~/constants/iconSize'
import { type CivilDate, todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { ImmutableInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/ImmutableInput'
import { PeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/PeriodFields'
import { SubFormLabel } from '~/pages/AdminOcctoMaster/tabs/_common/components/SubFormLabel'
import { availableOptions } from '~/pages/AdminOcctoMaster/tabs/_common/models/availableOptions'
import {
  planMasterRowDefaultValidFrom,
  resolveExpiryFallbackLabel,
} from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type BgEntry = {
  bgMasterId: string
  /** showPeriod=false (需要調達計画マスタ) の行には存在しない。 */
  validFrom?: { year: number; month: number; day: number }
  validTo?: { year: number; month: number; day: number }
}

type Props = {
  // Control<FieldValues> is the widest type that accepts any form schema while
  // keeping RHF's inference intact. We erase the specific schema type here
  // because this component is shared between two different form schemas.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  /** i18n ページキー namespace */
  i18nNs:
    | 'adminOcctoMaster.generationSalesPlanMaster'
    | 'adminOcctoMaster.demandProcurementPlanMaster'
  /**
   * BG マスタ一覧 (候補として使用)。
   * 区分フィルタは集約 RPC (BFF) が完了しているため、area narrow + 有効期間
   * (isActiveOn) narrow のみ client-side で行う (4526-plan-master-valid-period-filter
   * 設計書 §4)。
   */
  bgMasters: BgMasterRow[]
  /**
   * 全 BG マスタ一覧 (削除済み判定専用、4526-plan-master-valid-period-filter
   * 設計書 §4)。 License / area / 有効期間で一切絞らない `useListBgMasters()` の
   * 生データを渡す。 「(削除済み)」は絞り込み前のこの一覧に存在しない場合のみ表示し、
   * 有効期間外・License 変更後で `bgMasters` 候補から外れただけの実在 BG は
   * `{bgCode} - {bgName}` の fallback 表示を維持する (AC-06)。 list クエリ
   * 取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、
   * PR #5920 レビュー指摘)。
   */
  allBgMasters: BgMasterRow[] | undefined
  /** 自身のエリアコード (同一エリアの BG のみ候補に絞り込む) */
  selfAreaCode: AreaCode | undefined
  /**
   * proc-plan 代表BG の BgMasterId。
   * 渡された場合、bgs[0] に代表BG を自動挿入してロックする (削除/変更不可)。
   * backend が bgs[0].BgMasterID == repBgMasterID を強制するため必須送出。
   */
  repBgMasterId?: string
  /**
   * 行ごとの適用期間 (validFrom/validTo) 入力欄を表示するか。
   * デフォルト true (発電販売計画マスタの所属BGは行ごとに期間を持つ)。
   * 需要調達計画マスタの所属BG (DemandProcurementMasterBg) は行ごとの期間を
   * 持たず親マスタの適用期間に従うため false を渡す
   * (occto-master-valid-period-6tabs 設計書 §5.2/AC-12)。
   */
  showPeriod?: boolean
  /**
   * 「+ 追加」ボタンを非表示にするか (AC-09、spec AC-OMC-011)。
   * デフォルト false。 需要調達計画マスタは BG リストが代表BG 1 件固定の
   * ため、 Create/Edit モーダルが true を渡して JSX 自体を非描画にする
   * (disabled でなく非表示。 3938-occto-master-plan-spec-followup 設計書 §4)。
   */
  hideAddButton?: boolean
  /**
   * 発電販売計画マスタ専用: `deriveBgAddDisabledReason` の判定結果 (AC-06)。
   * `'noSubmitterBgs'` / `'noPlants'` の場合のみ追加ボタンを disabled にし、
   * 専用の inline note を表示する (`'keyMissing'` / `'hasBoth'` は無視 —
   * 親キー未選択時のガードは呼び出し側の他 UI が担う)。 需要調達計画マスタは
   * 渡さない (undefined)。
   */
  bgAddDisabledReason?: 'keyMissing' | 'noSubmitterBgs' | 'noPlants' | 'hasBoth'
}

export const PlanMasterBgsSection = ({
  control,
  i18nNs,
  bgMasters,
  allBgMasters,
  selfAreaCode,
  repBgMasterId,
  showPeriod = true,
  hideAddButton = false,
  bgAddDisabledReason,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { fields, append, remove, prepend, update } = useFieldArray({
    control,
    name: 'bgs',
  })

  // useWatch で bgs[] の最新値を取得 (useFieldArray の fields は re-render で stale になる)
  const rawWatchedBgs = useWatch({ control, name: 'bgs' }) as
    | BgEntry[]
    | undefined
  const watchedBgs = rawWatchedBgs ?? []

  // 行ごとの select は FormSelect が内部で Controller を張るため、 error 表示用の
  // fieldState はここで一括購読する (行ごとに Controller を二重に張らない)。
  const { errors } = useFormState({ control })
  const bgErrors = errors.bgs as
    | Array<{ bgMasterId?: { message?: string } } | undefined>
    | undefined

  // form 全体の validFrom (= レコードの適用開始日) を購読し、変更のたび候補フィルタを
  // 再計算する (4526-plan-master-valid-period-filter 設計書 §4 Q3-1)。
  const watchedRecordValidFrom = useWatch({ control, name: 'validFrom' }) as
    | CivilDate
    | undefined

  // proc-plan: 代表BG を bgs[0] に自動挿入・同期する。
  // - fields が空 → prepend で代表BG を先頭挿入
  // - fields[0] の bgMasterId が repBgMasterId と不一致 → update で差し替え (period 保持)
  useEffect(() => {
    if (repBgMasterId === undefined || repBgMasterId === '') return
    const first = watchedBgs[0]
    if (first?.bgMasterId === repBgMasterId) return
    if (fields.length === 0) {
      void prepend(
        showPeriod
          ? {
              bgMasterId: repBgMasterId,
              validFrom: planMasterRowDefaultValidFrom(watchedRecordValidFrom),
              validTo: undefined,
            }
          : { bgMasterId: repBgMasterId },
      )
    } else {
      // bgs[0] の BG を新 rep へ差し替える。period (validFrom/validTo) は保持する。
      update(0, {
        ...first,
        bgMasterId: repBgMasterId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repBgMasterId])

  /**
   * area + 有効期間 (isActiveOn) narrow 済み候補 (全行共通)。
   * 区分フィルタは集約 RPC (BFF) が完了しているため、client-side では area narrow +
   * レコードの適用開始日 (validFrom) 時点の有効期間 narrow のみ行う
   * (AC-01/AC-02、4526-plan-master-valid-period-filter 設計書 §4)。
   * validFrom が transient に undefined の間は narrow を skip する (fail-open)。
   */
  const filteredCandidates = useMemo(
    () =>
      bgMasters.filter(
        (bg) =>
          (selfAreaCode === undefined || bg.areaCode === selfAreaCode) &&
          (watchedRecordValidFrom === undefined ||
            isActiveOn(bg.validFrom, bg.validTo, watchedRecordValidFrom)),
      ),
    [bgMasters, selfAreaCode, watchedRecordValidFrom],
  )

  const noCandidates = filteredCandidates.length === 0
  // AC-06 (発電販売のみ): noSubmitterBgs / noPlants のみ追加ボタンを disable
  // する追加条件として扱う ('keyMissing' / 'hasBoth' / undefined は無視)。
  const noSubmitterBgs = bgAddDisabledReason === 'noSubmitterBgs'
  const noPlantsInSubmitterBgs = bgAddDisabledReason === 'noPlants'
  const addDisabled = noCandidates || noSubmitterBgs || noPlantsInSubmitterBgs

  return (
    <Box>
      {/* セクションヘッダー: ラベル + 追加ボタン (AC-09: hideAddButton=true は
          JSX 自体を非描画にする。 disabled と違い需要調達計画マスタの
          BG リストは「+ 追加」導線自体が存在しない)。
          design `.occto-sub-header` 準拠: border-top + padding-top + marginTop で
          直前セクションと区切り、 add ボタンは size="sm" で統一。 */}
      <HStack
        align="center"
        justify="space-between"
        marginTop={4}
        marginBottom={2}
        paddingTop={3}
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
      >
        <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
          {t(`page:${i18nNs}.form.bgs`)}
        </Text>
        {!hideAddButton && (
          <ButtonTooltip
            content={
              noCandidates ? t(`page:${i18nNs}.form.noBgCandidates`) : ''
            }
          >
            <TertiaryButton
              disabled={addDisabled}
              onClick={() => {
                void append(
                  showPeriod
                    ? {
                        bgMasterId: '',
                        validFrom: planMasterRowDefaultValidFrom(
                          watchedRecordValidFrom,
                        ),
                        validTo: undefined,
                      }
                    : { bgMasterId: '' },
                )
              }}
              size="sm"
              title={
                noCandidates ? t(`page:${i18nNs}.form.noBgCandidates`) : ''
              }
            >
              {/* 追加ボタンの ＋ は DS icon で描く。 従来は i18n 文言に全角の `＋ ` を
                  埋めていたためフォント依存で、 同じ役割の「入札追加」 (icon) と質感が
                  揃わず、 翻訳のたびに記号を書き写す必要もあった。
                  design css `.btn:not(...)>svg` = 20px。 */}
              <FaPlusRegular boxSize={iconSize.labeledButton} />
              {t(`page:${i18nNs}.form.addBg`)}
            </TertiaryButton>
          </ButtonTooltip>
        )}
      </HStack>

      {/* disabled 注記 (AC-06): ホバー tooltip でなく常時可視。
          複数条件が同時に true でも並置する。 */}
      {!hideAddButton && (
        <VStack align="stretch" gap={1} marginBottom={2}>
          {noCandidates && (
            <Text textStyle="fieldHint" color="Text.midEmphasis">
              {t(`page:${i18nNs}.form.noBgCandidates`)}
            </Text>
          )}
          {noSubmitterBgs && (
            <Text textStyle="fieldHint" color="Text.midEmphasis">
              {t(`page:${i18nNs}.form.noSubmitterBgs`)}
            </Text>
          )}
          {noPlantsInSubmitterBgs && (
            <Text textStyle="fieldHint" color="Text.midEmphasis">
              {t(`page:${i18nNs}.form.noPlantsInSubmitterBgs`)}
            </Text>
          )}
        </VStack>
      )}

      {/* BG エントリ一覧 */}
      <VStack align="stretch" gap={2}>
        {fields.length === 0 && (
          <Text color="Text.midEmphasis" fontSize="md">
            {t(`page:${i18nNs}.form.noBgsRegistered`)}
          </Text>
        )}
        {fields.map((field, idx) => {
          const entry: BgEntry = watchedBgs[idx] ?? {
            bgMasterId: '',
            validFrom: todayCivilDate(),
          }
          // proc-plan: bgs[0] は代表BG ロック (削除/変更不可)
          const isRepBg =
            repBgMasterId !== undefined && repBgMasterId !== '' && idx === 0

          /**
           * この行の bgMasterId を除いた使用済み BgMasterId セット。
           * 行ごとに都度計算して「既選択 ID がリストに残る」を保証する。
           */
          const usedIdsExcludingThisRow = new Set(
            watchedBgs
              .filter((_, i) => i !== idx)
              .map((b) => b.bgMasterId)
              .filter((id) => id !== ''),
          )

          /** この行向けの BG オプション (既選択 ID をリストに残す) */
          const bgOptsForRow = availableOptions(
            filteredCandidates,
            usedIdsExcludingThisRow,
            entry.bgMasterId,
          )

          /**
           * fallback option (= 選択済み値が bgOptsForRow に無い場合) 用のラベル。
           * 絞り込み前の全 BG マスタ (allBgMasters) に実在すれば
           * `{bgCode} - {bgName}` (対象日時点で終了していれば「（終了）」
           * サフィックス付き) を、 無ければ「(削除済み)」を返す
           * (AC-MOI-023、occto-master-fk-as-of-filter 設計書 Decision 2)。
           */
          const fallbackLabel = resolveExpiryFallbackLabel(
            allBgMasters,
            entry.bgMasterId,
            watchedRecordValidFrom,
            {
              idOf: (bg) => bg.id,
              labelOf: (bg) => `${bg.bgCode} - ${bg.bgName}`,
              validTo: (bg) => bg.validTo,
              deletedLabel: t(
                'page:adminOcctoMaster.common.masterFallback.deleted',
              ),
              expiredSuffix: t(
                'page:adminOcctoMaster.common.masterFallback.expiredSuffix',
              ),
            },
          )

          const bgError = bgErrors?.[idx]?.bgMasterId

          return (
            <Box
              key={field.id}
              padding={3}
              borderWidth="1px"
              borderColor={isRepBg ? 'blue.200' : 'Border.lowEmphasis'}
              borderRadius="md"
            >
              <Stack gap={3}>
                {/* BG select + 削除ボタン */}
                <HStack align="flex-end" gap={3}>
                  <Box flex={1}>
                    <FieldRoot invalid={bgError !== undefined}>
                      <SubFormLabel>
                        {t(`page:${i18nNs}.form.bgLabel`)}
                        {isRepBg && (
                          // 「🔒 代表BG」 は導線でなく選択状態の情報表示
                          // (代表 BG であることの識別マーク) のため chakra
                          // パレット容認 (2026-07-05 ユーザー判断)。 DS に
                          // 対応する識別用 token なし。
                          <Text
                            as="span"
                            marginLeft={1}
                            // eslint-disable-next-line no-restricted-syntax -- 2026-07-05 ユーザー判断で chakra パレット容認 (上記コメント参照)。 palette-scale ベースライン、semantic token への置換は別タスク
                            color="blue.600"
                            fontSize="xs"
                          >
                            {t(`page:${i18nNs}.form.repBgHint`)}
                          </Text>
                        )}
                      </SubFormLabel>
                      {isRepBg ? (
                        /* 代表BG はロック: disabled Input 表示。
                           参照先マスタが絞り込み前の全 BG マスタ
                           (allBgMasters) に存在しない (= 物理削除済み)
                           場合のみ「(削除済み)」を表示する。
                           有効期間外・License 変更後で bgMasters 候補
                           (isActiveOn narrow 後) から外れただけの実在 BG は
                           `{bgCode} - {bgName}` を維持する (AC-06、
                           4526-plan-master-valid-period-filter 設計書 §4)。 */
                        <ImmutableInput readOnly value={fallbackLabel ?? ''} />
                      ) : (
                        <FormSelect
                          control={control}
                          /* 既選択値が opts に無い場合のフォールバック表示。
                             絞り込み前の全 BG マスタ (allBgMasters) に存在すれば
                             `{bgCode} - {bgName}` を表示し (AC-06、有効期間外・
                             License 変更後の実在 BG)、無ければ「(削除済み)」を
                             表示する (= 物理削除された値のみ)。 未選択 ('') は
                             placeholder に戻す。 */
                          fallbackLabel={(v) =>
                            v === '' ? undefined : fallbackLabel
                          }
                          name={`bgs.${idx}.bgMasterId`}
                          options={bgOptsForRow.map((bg) => ({
                            value: bg.id,
                            label: `${bg.bgCode} - ${bg.bgName}`,
                          }))}
                          placeholder={t(`page:${i18nNs}.form.bgUnselected`)}
                        />
                      )}
                      {/* AC-04/AC-05: BG 未選択エラーの field-level 表示
                          (代表 BG はロックのため無選択にならず対象外)。 */}
                      {!isRepBg && bgError !== undefined && (
                        <FieldErrorText>{bgError.message}</FieldErrorText>
                      )}
                    </FieldRoot>
                  </Box>

                  {/* 削除ボタン (代表BG はロックで非表示) */}
                  {isRepBg ? (
                    <Box flexShrink={0} width="64px" />
                  ) : (
                    <TertiaryButton
                      flexShrink={0}
                      aria-label={t(`page:${i18nNs}.form.removeBg`)}
                      colorPalette="red"
                      onClick={() => {
                        remove(idx)
                      }}
                      size="sm"
                    >
                      {t(`page:${i18nNs}.form.removeBg`)}
                    </TertiaryButton>
                  )}
                </HStack>

                {/* 適用期間 (需要調達計画マスタの所属BGは行ごとの期間を持たず、
                    親マスタの適用期間に従うため非表示、AC-12) */}
                {showPeriod && (
                  <PeriodFields
                    basePath={`bgs.${idx}`}
                    clearAsNull
                    control={control}
                    i18nNs={i18nNs}
                    spacerWidth={isRepBg ? '64px' : '64px'}
                  />
                )}
              </Stack>
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}
